import type { PropsWithChildren } from 'react';
import { cn } from '@fshr/shared-react';
import BevSafeText from '../bev-safe-text';
import Image from '../image';
import HorizontalLine from '../horizontal-line';

type SearchModalCardVariant = 'featured' | 'search-property' | 'search-other';

type SearchModalCardWrapperProps = PropsWithChildren<{ url?: string }>;

const wrapperClassName = 'flex flex-row spacing-inline-s justify-start items-center w-full';

function SearchModalContentCardWrapper({ url, children }: SearchModalCardWrapperProps) {
	if (url) {
		return (
			<a href={url} className={cn(wrapperClassName)}>
				{children}
			</a>
		);
	}
	return (
		<div className={cn(wrapperClassName)} role="group">
			{children}
		</div>
	);
}

type SearchModalCardProps = {
	image?: string;
	title?: string;
	description?: string;
	url?: string;
	showSeparator?: boolean;
	variant: SearchModalCardVariant;
};

const IMAGE_SIZE_CLASS = 'aspect-[1/1] w-[75px] h-[75px] shrink-0';
const DEFAULT_STRONG_CLASS = '[&_strong]:font-bold [&_strong]:text-text-primary';

export function SearchModalCard({
	image,
	title,
	description,
	url,
	showSeparator = false,
	variant,
}: SearchModalCardProps) {
	const isSearchOther = variant === 'search-other';
	const hasSingleLine = !title || !description;
	const titleClassName = cn(
		'text-wrap',
		isSearchOther ? 'text-text-disabled' : 'text-text-primary',
		isSearchOther ? DEFAULT_STRONG_CLASS : undefined
	);
	const descriptionClassName = cn(
		'text-wrap text-text-secondary',
		isSearchOther ? DEFAULT_STRONG_CLASS : undefined
	);

	return (
		<div className={cn('flex flex-col w-full gap-[15px]')}>
			<SearchModalContentCardWrapper url={url}>
				{image ? <Image src={image} roughSize="s" className={cn(IMAGE_SIZE_CLASS)} /> : null}
				<div
					className={cn(
						'flex flex-col spacing-stack-2xs min-w-[0px] flex-1',
						hasSingleLine && 'justify-center'
					)}
				>
					{title ? (
						isSearchOther ? (
							<div className="text-subtitle2">
								<p className={titleClassName} dangerouslySetInnerHTML={{ __html: title }} />
							</div>
						) : (
							<p className={titleClassName}>
								<span className="text-subtitle2">
									<BevSafeText>{title}</BevSafeText>
								</span>
							</p>
						)
					) : null}
					{description ? (
						isSearchOther ? (
							<div className="text-caption">
								<p
									className={descriptionClassName}
									dangerouslySetInnerHTML={{ __html: description }}
								/>
							</div>
						) : (
							<p className={descriptionClassName}>
								<span className="text-caption">
									<BevSafeText>{description}</BevSafeText>
								</span>
							</p>
						)
					) : null}
				</div>
			</SearchModalContentCardWrapper>
			{showSeparator ? <HorizontalLine additionalClasses="border-border-subtle" /> : null}
		</div>
	);
}
